// src/utils/generateFundReceipt.js

import { jsPDF } from "jspdf";

/**
 * Convert Firestore Timestamp, Date or date string
 * into a JavaScript Date.
 */
const toJavaScriptDate = (value) => {
    try {
        if (typeof value?.toDate === "function") {
            return value.toDate();
        }

        if (value instanceof Date) {
            return value;
        }

        if (typeof value === "string" || typeof value === "number") {
            const convertedDate = new Date(value);

            if (!Number.isNaN(convertedDate.getTime())) {
                return convertedDate;
            }
        }
    } catch (error) {
        console.error("Receipt date conversion error:", error);
    }

    return new Date();
};

/**
 * Format receipt date in Indian format.
 */
const formatReceiptDate = (value) => {
    const date = toJavaScriptDate(value);

    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

/**
 * Remove characters that are unsuitable for file names.
 */
const cleanFileName = (value) =>
    String(value || "")
        .trim()
        .replace(/[<>:"/\\|?*]+/g, "-")
        .replace(/\s+/g, "_");

/**
 * Capitalize the first letter of a string.
 */
const capitalize = (value) => {
    const text = String(value || "");

    if (!text) return "";

    return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Convert a number below 1000 into words.
 */
const convertBelowThousand = (amount) => {
    const ones = [
        "",
        "One",
        "Two",
        "Three",
        "Four",
        "Five",
        "Six",
        "Seven",
        "Eight",
        "Nine",
        "Ten",
        "Eleven",
        "Twelve",
        "Thirteen",
        "Fourteen",
        "Fifteen",
        "Sixteen",
        "Seventeen",
        "Eighteen",
        "Nineteen",
    ];

    const tens = [
        "",
        "",
        "Twenty",
        "Thirty",
        "Forty",
        "Fifty",
        "Sixty",
        "Seventy",
        "Eighty",
        "Ninety",
    ];

    let number = Number(amount) || 0;
    const result = [];

    if (number >= 100) {
        result.push(`${ones[Math.floor(number / 100)]} Hundred`);
        number %= 100;
    }

    if (number >= 20) {
        result.push(tens[Math.floor(number / 10)]);

        if (number % 10 > 0) {
            result.push(ones[number % 10]);
        }
    } else if (number > 0) {
        result.push(ones[number]);
    }

    return result.join(" ");
};

/**
 * Convert whole number using Indian number system.
 */
const convertIndianWholeNumber = (input) => {
    let number = Math.floor(Number(input) || 0);

    if (number === 0) {
        return "Zero";
    }

    const parts = [];

    const crore = Math.floor(number / 10000000);

    if (crore > 0) {
        parts.push(`${convertIndianWholeNumber(crore)} Crore`);
        number %= 10000000;
    }

    const lakh = Math.floor(number / 100000);

    if (lakh > 0) {
        parts.push(`${convertBelowThousand(lakh)} Lakh`);
        number %= 100000;
    }

    const thousand = Math.floor(number / 1000);

    if (thousand > 0) {
        parts.push(`${convertBelowThousand(thousand)} Thousand`);
        number %= 1000;
    }

    if (number > 0) {
        parts.push(convertBelowThousand(number));
    }

    return parts.join(" ").replace(/\s+/g, " ").trim();
};

/**
 * Convert amount into Indian Rupees and Paise words.
 *
 * Examples:
 * 1250    -> One Thousand Two Hundred Fifty Rupees Only
 * 1250.50 -> One Thousand Two Hundred Fifty Rupees
 *            and Fifty Paise Only
 */
export const amountToIndianWords = (inputAmount) => {
    const number = Number(inputAmount);

    if (!Number.isFinite(number) || number < 0) {
        return "";
    }

    const roundedAmount = Math.round((number + Number.EPSILON) * 100) / 100;
    const rupees = Math.floor(roundedAmount);
    const paise = Math.round((roundedAmount - rupees) * 100);

    let result = `${convertIndianWholeNumber(rupees)} Rupees`;

    if (paise > 0) {
        result += ` and ${convertIndianWholeNumber(paise)} Paise`;
    }

    return `${result} Only`;
};

/**
 * Format amount as Indian currency without relying on the
 * Rupee glyph inside the default PDF font.
 */
const formatAmount = (value) => {
    const amount = Number(value) || 0;

    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

/**
 * Generate a receipt number when an existing receipt number
 * was not entered.
 *
 * This fallback does not modify Firestore.
 */
const getReceiptNumber = (fund) => {
    const existingReceiptNumber = String(
        fund?.receiptNo || ""
    ).trim();

    if (existingReceiptNumber) {
        return existingReceiptNumber;
    }

    const documentPart = String(fund?.id || "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .slice(0, 10)
        .toUpperCase();

    return documentPart
        ? `RCPT-${documentPart}`
        : `RCPT-${Date.now()}`;
};
const normalizeUnitValue = (value) => {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^a-z0-9]/g, "");
};

const normalizeFlatForComparison = (
    blockName,
    flatLabel
) => {
    const normalizedBlock =
        normalizeUnitValue(blockName);

    let normalizedFlat =
        normalizeUnitValue(flatLabel);

    if (
        normalizedBlock &&
        normalizedFlat.startsWith(
            normalizedBlock
        )
    ) {
        normalizedFlat =
            normalizedFlat.slice(
                normalizedBlock.length
            );
    }

    return normalizedFlat;
};

const formatResidentUnit = (
    blockName,
    flatLabel
) => {
    const cleanBlockName = String(
        blockName || ""
    ).trim();

    const cleanFlatLabel = String(
        flatLabel || ""
    ).trim();

    if (
        !cleanBlockName &&
        !cleanFlatLabel
    ) {
        return "";
    }

    if (!cleanBlockName) {
        return cleanFlatLabel;
    }

    if (!cleanFlatLabel) {
        return cleanBlockName;
    }

    const normalizedBlock =
        normalizeUnitValue(
            cleanBlockName
        );

    const normalizedFlat =
        normalizeUnitValue(
            cleanFlatLabel
        );

    if (
        normalizedFlat.startsWith(
            normalizedBlock
        )
    ) {
        return cleanFlatLabel;
    }

    return `${cleanBlockName}-${cleanFlatLabel}`;
};

/**
 * Identify who paid the resident collection.
 */

const getResidentPaidBy = (
    fund,
    residents = []
) => {
    const blockName = String(
        fund?.blockName || ""
    ).trim();

    const flatLabel = String(
        fund?.flatLabel || ""
    ).trim();

    const payerUid = String(
        fund?.payerUid || ""
    ).trim();

    const savedPaidByName = String(
        fund?.paidByName || ""
    ).trim();

    const safeResidents = Array.isArray(residents)
        ? residents
        : [];

    const normalizedFundBlock =
        normalizeUnitValue(blockName);

    const normalizedFundFlat =
        normalizeFlatForComparison(
            blockName,
            flatLabel
        );

    const matchingResident =
        safeResidents.find((resident) => {
            const residentUid = String(
                resident?.uid || ""
            ).trim();

            if (
                payerUid &&
                residentUid &&
                payerUid === residentUid
            ) {
                return true;
            }

            const residentBlockName = String(
                resident?.unit?.blockName || ""
            ).trim();

            const residentFlatLabel = String(
                resident?.unit?.flatLabel || ""
            ).trim();

            const normalizedResidentBlock =
                normalizeUnitValue(
                    residentBlockName
                );

            const normalizedResidentFlat =
                normalizeFlatForComparison(
                    residentBlockName,
                    residentFlatLabel
                );

            return (
                normalizedResidentBlock ===
                normalizedFundBlock &&
                normalizedResidentFlat ===
                normalizedFundFlat
            );
        });

    console.log("Fund Data", fund);
    console.log("Residents", residents);
    console.log("Matching Resident", matchingResident);

    const matchedResidentName = String(
        matchingResident?.fullName ||
        matchingResident?.displayName ||
        matchingResident?.name ||
        ""
    ).trim();

    const normalizedSavedName =
        normalizeUnitValue(
            savedPaidByName
        );

    const normalizedUnit =
        normalizeUnitValue(
            formatResidentUnit(
                blockName,
                flatLabel
            )
        );

    const savedNameIsOnlyUnit =
        !savedPaidByName ||
        normalizedSavedName ===
        normalizedUnit ||
        normalizedSavedName ===
        normalizeUnitValue(
            flatLabel
        );

    const residentName =
        matchedResidentName ||
        String(fund?.otherTitle || "").trim() ||
        savedPaidByName ||
        "Resident";

    const unit = formatResidentUnit(
        blockName,
        flatLabel
    );

    return unit
        ? `${residentName} (${unit})`
        : residentName;
};
/**
 * Get the person from whom the amount was received.
 */

// const getReceivedFrom = (
//     fund,
//     residents = []
// ) => {
//     const sourceType = String(
//         fund?.sourceType ||
//         "resident"
//     )
//         .trim()
//         .toLowerCase();

//     const payerType = String(
//         fund?.payerType || ""
//     )
//         .trim()
//         .toLowerCase();

//     const hasResidentUnit =
//         Boolean(
//             String(
//                 fund?.blockName || ""
//             ).trim()
//         ) &&
//         Boolean(
//             String(
//                 fund?.flatLabel || ""
//             ).trim()
//         );

//     if (
//         sourceType === "resident" ||
//         payerType === "resident" ||
//         hasResidentUnit
//     ) {
//         return getResidentPaidBy(
//             fund,
//             residents
//         );
//     }

//     return (
//         String(
//             fund?.paidByName || ""
//         ).trim() ||
//         String(
//             fund?.otherTitle || ""
//         ).trim() ||
//         String(
//             fund?.otherType || ""
//         ).trim() ||
//         "Not specified"
//     );
// };
const getReceivedFrom = (
    fund,
    residents = []
) => {
    const sourceType = String(
        fund?.sourceType || ""
    )
        .trim()
        .toLowerCase();

    if (sourceType === "other") {
        return (
            String(
                fund?.paidByName || ""
            ).trim() ||
            "Not specified"
        );
    }

    return getResidentPaidBy(
        fund,
        residents
    );
};



/**
 * Get the committee member or collector who received the amount.
 */
const getReceivedBy = (fund) => {
    const sourceType = fund?.sourceType || "resident";

    if (sourceType === "other") {
        return (
            String(fund?.receivedByName || "").trim() ||
            String(fund?.paidByName || "").trim() ||
            "Not specified"
        );
    }

    return (
        String(fund?.receivedByName || "").trim() ||
        String(fund?.paidByName || "").trim() ||
        "Not specified"
    );
};

/**
 * Draw a label/value row and return the next Y coordinate.
 */
const drawReceiptRow = ({
    pdf,
    label,
    value,
    y,
    pageWidth,
    labelWidth = 43,
}) => {
    const leftMargin = 20;
    const rightMargin = 20;
    const valueX = leftMargin + labelWidth;
    const availableWidth = pageWidth - valueX - rightMargin;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10.5);
    pdf.setTextColor(45, 55, 72);
    pdf.text(label, leftMargin, y);

    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(25, 25, 25);

    const wrappedText = pdf.splitTextToSize(
        String(value || "Not specified"),
        availableWidth
    );

    pdf.text(wrappedText, valueX, y);

    const rowHeight = Math.max(8, wrappedText.length * 6);

    return y + rowHeight;
};

/**
 * Generates and downloads one fund receipt as a PDF.
 */
export const generateFundReceipt = ({
    fund,
    community,
    event,
    residents = [],
}) => {
    if (!fund) {
        throw new Error("Fund information is required.");
    }

    const amount = Number(fund.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error("A valid receipt amount is required.");
    }

    const communityName =
        String(
            community?.name ||
            community?.communityName ||
            community?.data?.name ||
            ""
        ).trim() || "Community";

    const communityAddress = String(
        community?.address ||
        community?.data?.address ||
        ""
    ).trim();

    const eventName =
        String(event?.name || event?.title || "").trim() ||
        "Community Event";

    // const residentName =
    // String(event?.residentName || event?.name || "").trim();

    const receiptNumber = getReceiptNumber(fund);
    const receiptDate = formatReceiptDate(
        fund.receiptDate ||
        fund.createdAt ||
        fund.updatedAt
    );

    const receivedFrom = getReceivedFrom(fund, residents);
    const receivedBy = getReceivedBy(fund);
    const paymentMethod =
        String(fund.method || "").trim() || "Not specified";
    const sourceType = fund.sourceType || "resident";

    const collectionType =
        sourceType === "other"
            ? String(
                fund.otherType ||
                fund.otherTitle ||
                "Other Collection"
            )
            : "Contribution";

    const amountInWords = amountToIndianWords(amount);

    const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const leftMargin = 15;
    const receiptWidth = pageWidth - 30;

    /*
     * Main border.
     */
    pdf.setDrawColor(48, 91, 155);
    pdf.setLineWidth(0.8);
    pdf.roundedRect(
        leftMargin,
        15,
        receiptWidth,
        245,
        3,
        3
    );

    /*
     * Header background.
     */
    pdf.setFillColor(48, 91, 155);
    pdf.roundedRect(
        leftMargin,
        15,
        receiptWidth,
        35,
        3,
        3,
        "F"
    );

    /*
     * Fix the lower header corners visually.
     */
    pdf.rect(
        leftMargin,
        40,
        receiptWidth,
        10,
        "F"
    );

    /*
     * Community name.
     */
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);

    const communityTitleLines = pdf.splitTextToSize(
        communityName,
        receiptWidth - 20
    );

    pdf.text(
        communityTitleLines,
        pageWidth / 2,
        32,
        {
            align: "center",
        }
    );

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);

    if (communityAddress) {
        const addressLines = pdf.splitTextToSize(
            communityAddress,
            receiptWidth - 25
        );

        pdf.text(
            addressLines.slice(0, 2),
            pageWidth / 2,
            39,
            {
                align: "center",
            }
        );
    }

    /*
     * Receipt title.
     */
    pdf.setTextColor(30, 30, 30);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text(
        "PAYMENT RECEIPT",
        pageWidth / 2,
        64,
        {
            align: "center",
        }
    );

    pdf.setDrawColor(48, 91, 155);
    pdf.setLineWidth(0.4);
    pdf.line(70, 68, pageWidth - 70, 68);

    /*
     * Receipt number and date.
     */
    pdf.setFontSize(10);
    pdf.setTextColor(60, 60, 60);

    pdf.setFont("helvetica", "bold");
    pdf.text("Receipt No:", 20, 80);

    pdf.setFont("helvetica", "normal");
    pdf.text(receiptNumber, 45, 80);

    //    pdf.setFont("helvetica", "bold");
    //   pdf.text("Name:", 30, 80);

    //   pdf.setFont("helvetica", "normal");
    //   pdf.text(residentName, 45, 80);

    pdf.setFont("helvetica", "bold");
    pdf.text("Date:", pageWidth - 68, 80);

    pdf.setFont("helvetica", "normal");
    pdf.text(receiptDate, pageWidth - 52, 80);

    /*
     * Receipt information box.
     */
    pdf.setFillColor(247, 249, 252);
    pdf.setDrawColor(210, 218, 230);
    pdf.roundedRect(
        18,
        90,
        pageWidth - 36,
        105,
        2,
        2,
        "FD"
    );

    let y = 105;

    y = drawReceiptRow({
        pdf,
        label: "Community:",
        value: communityName,
        y,
        pageWidth,
    });

    y = drawReceiptRow({
        pdf,
        label: "Event:",
        value: eventName,
        y,
        pageWidth,
    });


    y = drawReceiptRow({
        pdf,
        label: "Received From:",
        value: capitalize(receivedFrom),
        y,
        pageWidth,
    });

    y = drawReceiptRow({
        pdf,
        label: "Collection Type:",
        value: capitalize(collectionType),
        y,
        pageWidth,
    });

    const fundPurpose = String(
        fund?.otherTitle || ""
    ).trim();

    if (
        sourceType === "other" &&
        fundPurpose
    ) {
        let purposeLabel = "Purpose:";

        switch (
        String(fund?.otherType || "")
            .trim()
            .toLowerCase()
        ) {
            case "donation":
                purposeLabel = "Donation For:";
                break;

            case "Sponsor":
            case "sponsor":
                purposeLabel = "Sponsor:";
                break;

            case "gift":
                purposeLabel = "Gift For:";
                break;

            case "sale":
                purposeLabel = "Sale Item:";
                break;

            default:
                purposeLabel = "Purpose:";
        }

        y = drawReceiptRow({
            pdf,
            label: purposeLabel,
            value: fundPurpose,
            y,
            pageWidth,
        });
    }

    y = drawReceiptRow({
        pdf,
        label: "Amount:",
        value: `INR ${formatAmount(amount)}`,
        y,
        pageWidth,
    });

    y = drawReceiptRow({
        pdf,
        label: "Amount in Words:",
        value: amountInWords,
        y,
        pageWidth,
    });

    y = drawReceiptRow({
        pdf,
        label: "Payment Mode:",
        value: paymentMethod,
        y,
        pageWidth,
    });

    /*
     * Notes for other funds.
     */
    if (
        sourceType === "other" &&
        String(fund.otherNotes || "").trim()
    ) {
        y = drawReceiptRow({
            pdf,
            label: "Notes:",
            value: fund.otherNotes,
            y,
            pageWidth,
        });
    }

    /*
     * Amount highlight.
     */
    pdf.setFillColor(231, 243, 232);
    pdf.setDrawColor(76, 145, 80);
    pdf.roundedRect(
        55,
        205,
        pageWidth - 110,
        22,
        2,
        2,
        "FD"
    );

    pdf.setTextColor(38, 92, 41);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text(
        `INR ${formatAmount(amount)}`,
        pageWidth / 2,
        219,
        {
            align: "center",
        }
    );


    /*
     * Signature Section
     */
    pdf.setDrawColor(90, 90, 90);
    pdf.setLineWidth(0.3);

    pdf.line(
        pageWidth - 75,
        240,
        pageWidth - 20,
        240
    );

    /*
     * Received By Name
     */
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(30, 30, 30);

    pdf.text(
        receivedBy || "Committee Member",
        pageWidth - 47.5,
        237,
        {
            align: "center",
        }
    );

    /*
     * Signature Label
     */
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(70, 70, 70);

    pdf.text(
        "Received By",
        pageWidth - 47.5,
        247,
        {
            align: "center",
        }
    );

    //   /*
    //    * Footer.
    //    */
    //   pdf.setDrawColor(220, 220, 220);
    //   pdf.line(20, pageHeight - 25, pageWidth - 20, pageHeight - 25);

    //   pdf.setFontSize(8);
    //   pdf.setTextColor(110, 110, 110);
    // //   pdf.text(
    // //     "This is a computer-generated receipt.",
    // //     pageWidth / 2,
    // //     pageHeight - 18,
    // //     {
    // //       align: "center",
    // //     }
    // //   );

    // //   pdf.text(
    // //     `${communityName} - ${eventName}`,
    // //     pageWidth / 2,
    // //     pageHeight - 13,
    // //     {
    // //       align: "center",
    // //     }
    // //   );

    // pdf.setFontSize(8);

    // pdf.setTextColor(
    //   110,
    //   110,
    //   110
    // );

    // pdf.text(
    //   "This is a computer-generated receipt.",
    //   pageWidth / 2,
    //   pageHeight - 22,
    //   {
    //     align: "center",
    //   }
    // );

    // pdf.text(
    //   "Generated from",
    //   pageWidth / 2,
    //   pageHeight - 17,
    //   {
    //     align: "center",
    //   }
    // );

    // pdf.setFont(
    //   "helvetica",
    //   "bold"
    // );

    // pdf.text(
    //   "Lupie Service App",
    //   pageWidth / 2,
    //   pageHeight - 12,
    //   {
    //     align: "center",
    //   }
    // );

    // pdf.setFont(
    //   "helvetica",
    //   "normal"
    // );

    // pdf.setTextColor(
    //   0,
    //   102,
    //   204
    // );

    // pdf.textWithLink(
    //   "www.lupoeapp.com",
    //   pageWidth / 2 - 15,
    //   pageHeight - 7,
    //   {
    //     url: "https://lupieapp.com"
    //   }
    // );

    /*
     * Footer Section
     */
    const footerLeft = 20;
    const footerRight = pageWidth - 20;
    const footerLineY = pageHeight - 20;

    /*
     * Text ABOVE the footer line
     */
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);

    pdf.text(
        "This is a computer-generated receipt.",
        pageWidth / 2,
        footerLineY - 12,
        {
            align: "center",
        }
    );

    pdf.text(
        `${communityName} - ${eventName}`,
        pageWidth / 2,
        footerLineY - 7,
        {
            align: "center",
        }
    );

    /*
     * Footer separator line
     */
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.3);

    pdf.line(
        footerLeft,
        footerLineY,
        footerRight,
        footerLineY
    );

    /*
     * Bottom Left
     */
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(110, 110, 110);

    pdf.text(
        "Generated from : ",
        footerLeft,
        footerLineY + 5
    );

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);

    pdf.text(
        "Lupie Service App",
        footerLeft + 20,
        footerLineY + 5
    );

    /*
     * Bottom Right URL
     */
    const applicationUrl =
        "https://lupieapp.com";

    const applicationUrlText =
        "lupieapp.com";

    const urlWidth =
        pdf.getTextWidth(
            applicationUrlText
        );

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(
        0,
        102,
        204
    );

    pdf.textWithLink(
        applicationUrlText,
        footerRight - urlWidth,
        footerLineY + 5,
        {
            url: applicationUrl,
        }
    );

    /*
     * Underline URL
     */
    pdf.setDrawColor(
        0,
        102,
        204
    );

    pdf.line(
        footerRight - urlWidth,
        footerLineY + 5.5,
        footerRight,
        footerLineY + 5.5
    );

    /*
     * PDF document properties.
     */
    pdf.setProperties({
        title: `Receipt ${receiptNumber}`,
        subject: `${eventName} fund receipt`,
        author: communityName,
        creator: "Community Management Application",
        keywords: `receipt, ${communityName}, ${eventName}`,
    });

    const receiptFileName = [
        "Receipt",
        cleanFileName(receiptNumber),
        cleanFileName(eventName),
    ]
        .filter(Boolean)
        .join("_");

    pdf.save(`${receiptFileName}.pdf`);
};